import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import StoriesHero from './components/StoriesHero';
import CaseStudyCard from './components/CaseStudyCard';
import ClientLogosGrid from './components/ClientLogosGrid';
import TestimonialsSection from './components/TestimonialsSection';
import { StoriesHeroSkeleton, ClientLogosSkeleton, CaseStudySkeleton, TestimonialsSkeleton } from '../../components/SkeletonLoader';

const springEasing = [0.22, 1, 0.36, 1];

const caseStudies = [
  {
    company: 'NorTrans AS',
    industry: 'Transport & Logistikk',
    logoInitials: 'NT',
    logoColor: 'rgba(0,79,152,0.15)',
    logoTextColor: '#004F98',
    challenge: 'NorTrans AS opererte 28 lastebiler på daglige ruter mellom Oslo og Bergen. Bomkostnadene var uforutsigbare og vanskelige å budsjettere, noe som skapte store utfordringer for lønnsomheten.',
    costBefore: 87400,
    costAfter: 54200,
    vehicleCount: 28,
    solutions: [
      'Implementering av sanntids ruteoptimalisering for alle 28 kjøretøy',
      'Automatisk rapportering av bomkostnader per oppdrag og kunde',
      'Varslingssystem for uventede bomstasjoner og prisendringer',
      'Månedlig analyse og optimalisering av faste ruter'
    ]
  },
  {
    company: 'Bergen Buss',
    industry: 'Kollektivtransport',
    logoInitials: 'BB',
    logoColor: 'rgba(255,87,34,0.12)',
    logoTextColor: '#FF5722',
    challenge: 'Bergen Buss drifter 45 busser i Hordaland-regionen. Med hyppige ruteendringer og sesongvariasjoner var det nesten umulig å holde oversikt over de faktiske bomkostnadene per rute.',
    costBefore: 142000,
    costAfter: 88000,
    vehicleCount: 45,
    solutions: [
      'Flåtestyring med GPS-sporing og bomkostnadsberegning i sanntid',
      'Integrasjon med eksisterende ruteplanleggingssystem',
      'Automatisk fakturering til Skyss med detaljerte bomkostnadsrapporter',
      'Sesongbasert ruteoptimalisering for å minimere bompasseringer'
    ]
  },
  {
    company: 'Arktis Logistikk',
    industry: 'Arktisk Logistikk',
    logoInitials: 'AL',
    logoColor: 'rgba(61,26,92,0.15)',
    logoTextColor: '#9B59B6',
    challenge: 'Arktis Logistikk kjører varer til Nord-Norge med 15 spesialkjøretøy. De lange avstandene og mange bomstasjoner langs E6 og E10 gjorde kostnadsstyringen svært kompleks og tidkrevende.',
    costBefore: 68500,
    costAfter: 45200,
    vehicleCount: 15,
    solutions: [
      'Spesialtilpasset ruteoptimalisering for nordnorske veier og bomstasjoner',
      'Integrasjon med AutoPASS for automatisk betaling og registrering',
      'Detaljert kostnadsoversikt per kjøretøy, rute og oppdrag',
      'Prediktiv analyse for sesongbaserte prisendringer i nord'
    ]
  }
];

const CustomerStories = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 1100);
    return () => clearTimeout(timer);
  }, []);

  const navLinks = [
    ['#features', 'Tjenester', '/landing-page'],
    ['/toll-calculator', 'Priser', null],
    ['/contact', 'Kontakt', null],
    ['/trust-and-security', 'Sikkerhet', null],
    ['/about-us', 'Om oss', null],
    ['/customer-stories', 'Kunder', null],
    ['/faq', 'FAQ', null],
  ];

  const handleNavClick = (href, fallback) => {
    setMobileOpen(false);
    if (href?.startsWith('#')) {
      navigate(fallback || '/landing-page');
    } else {
      navigate(href);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--color-background)', position: 'relative', overflow: 'hidden' }}>
      {/* Background geometry */}
      <div style={{ position: 'absolute', top: '-80px', right: '-80px', width: '400px', height: '400px', border: '2px solid rgba(0,79,152,0.06)', borderRadius: '2px', transform: 'rotate(18deg)', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', top: '200px', right: '-40px', width: '200px', height: '200px', border: '1px solid rgba(255,87,34,0.07)', borderRadius: '2px', transform: 'rotate(12deg)', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', bottom: '80px', left: '-60px', width: '280px', height: '280px', border: '2px solid rgba(0,79,152,0.05)', borderRadius: '2px', transform: 'rotate(-14deg)', pointerEvents: 'none' }} />
      {/* Navigation */}
      <nav style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100, height: '64px', background: 'rgba(10,12,16,0.92)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(224,228,232,0.06)', display: 'flex', alignItems: 'center', padding: '0 32px' }}>
        <div style={{ maxWidth: '1440px', width: '100%', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <button onClick={() => navigate('/landing-page')} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', padding: 0 }}>
            <div style={{ width: '28px', height: '28px', background: '#FF5722', borderRadius: '2px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2.5"><polygon points="3 11 22 2 13 21 11 13 3 11" /></svg>
            </div>
            <span style={{ fontFamily: 'var(--font-heading)', fontSize: '16px', fontWeight: 700, color: 'var(--color-foreground)' }}>Veiassistent</span>
          </button>

          <div className="hidden-mobile" style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            {navLinks?.map(([href, label, fallback]) => {
              const isActive = !href?.startsWith('#') && location?.pathname === href;
              return (
                <button key={href} onClick={() => handleNavClick(href, fallback)}
                  style={{ background: 'none', border: 'none', borderBottom: isActive ? '2px solid #FF5722' : '2px solid transparent', padding: '2px 0', fontFamily: 'var(--font-heading)', fontSize: '12px', fontWeight: isActive ? 700 : 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: isActive ? '#FF5722' : 'var(--color-muted-foreground)', cursor: 'pointer', transition: 'color 150ms ease, border-color 150ms ease' }}
                  onMouseEnter={e => { if (!isActive) e.currentTarget.style.color = 'var(--color-foreground)'; }}
                  onMouseLeave={e => { if (!isActive) e.currentTarget.style.color = 'var(--color-muted-foreground)'; }}
                >{label}</button>
              );
            })}
          </div>

          <div className="hidden-mobile" style={{ display: 'flex', gap: '12px' }}>
            <button onClick={() => navigate('/login')} style={{ background: 'transparent', border: '1px solid rgba(224,228,232,0.2)', borderRadius: '2px', padding: '8px 20px', fontFamily: 'var(--font-heading)', fontSize: '13px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--color-foreground)', cursor: 'pointer' }}>Logg inn</button>
            <button onClick={() => navigate('/register')} style={{ background: '#FF5722', border: '2px solid #FF5722', borderRadius: '2px', padding: '8px 20px', fontFamily: 'var(--font-heading)', fontSize: '13px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#FFFFFF', cursor: 'pointer' }}>Registrer deg</button>
          </div>

          {/* Hamburger */}
          <button className="show-mobile" onClick={() => setMobileOpen(p => !p)} style={{ display: 'none', background: 'none', border: 'none', cursor: 'pointer', padding: '8px', zIndex: 110 }}>
            <div style={{ width: '24px', height: '20px', position: 'relative', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <motion.span animate={mobileOpen ? { rotate: 45, y: 8 } : { rotate: 0, y: 0 }} transition={{ duration: 0.3, ease: springEasing }} style={{ display: 'block', height: '2px', width: '100%', background: mobileOpen ? '#FF5722' : 'var(--color-foreground)', borderRadius: '2px', transformOrigin: 'center' }} />
              <motion.span animate={mobileOpen ? { opacity: 0, scaleX: 0 } : { opacity: 1, scaleX: 1 }} transition={{ duration: 0.2 }} style={{ display: 'block', height: '2px', width: '100%', background: 'var(--color-foreground)', borderRadius: '2px' }} />
              <motion.span animate={mobileOpen ? { rotate: -45, y: -8 } : { rotate: 0, y: 0 }} transition={{ duration: 0.3, ease: springEasing }} style={{ display: 'block', height: '2px', width: '100%', background: mobileOpen ? '#FF5722' : 'var(--color-foreground)', borderRadius: '2px', transformOrigin: 'center' }} />
            </div>
          </button>
        </div>
      </nav>
      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div key="overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setMobileOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 105, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(2px)' }} />
            <motion.div key="panel" initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }} transition={{ duration: 0.4, ease: springEasing }} style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: '280px', zIndex: 106, background: 'rgba(26,29,32,0.98)', backdropFilter: 'blur(20px)', borderLeft: '1px solid rgba(224,228,232,0.08)', display: 'flex', flexDirection: 'column', paddingTop: '88px', paddingBottom: '32px', paddingLeft: '32px', paddingRight: '32px' }}>
              <nav style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '32px' }}>
                {navLinks?.map(([href, label, fallback], i) => {
                  const isActive = !href?.startsWith('#') && location?.pathname === href;
                  return (<motion.button key={href} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 + i * 0.05, duration: 0.35, ease: springEasing }} onClick={() => handleNavClick(href, fallback)} style={{ background: isActive ? 'rgba(255,87,34,0.1)' : 'none', border: 'none', borderLeft: isActive ? '3px solid #FF5722' : '3px solid transparent', borderRadius: '0 4px 4px 0', padding: '12px 16px', fontFamily: 'var(--font-heading)', fontSize: '13px', fontWeight: isActive ? 700 : 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: isActive ? '#FF5722' : 'var(--color-muted-foreground)', cursor: 'pointer', textAlign: 'left' }}>{label}</motion.button>);
                })}
              </nav>
              <div style={{ height: '1px', background: 'rgba(224,228,232,0.08)', marginBottom: '28px' }} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <button onClick={() => { setMobileOpen(false); navigate('/login'); }} style={{ background: 'transparent', border: '1px solid rgba(224,228,232,0.2)', borderRadius: '2px', padding: '12px 24px', fontFamily: 'var(--font-heading)', fontSize: '13px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--color-foreground)', cursor: 'pointer' }}>Logg inn</button>
                <button onClick={() => { setMobileOpen(false); navigate('/register'); }} style={{ background: '#FF5722', border: '2px solid #FF5722', borderRadius: '2px', padding: '12px 24px', fontFamily: 'var(--font-heading)', fontSize: '13px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#FFFFFF', cursor: 'pointer' }}>Registrer deg</button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
      <main className="pt-[84px]" style={{ position: 'relative', zIndex: 1 }}>
        {isLoading ? (
          <>
            <StoriesHeroSkeleton />
            <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
              <div style={{ height: '1px', background: 'linear-gradient(to right, transparent, rgba(255,87,34,0.15), transparent)' }} />
            </div>
            <ClientLogosSkeleton />
            <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
              <div style={{ height: '1px', background: 'linear-gradient(to right, transparent, rgba(0,79,152,0.15), transparent)' }} />
            </div>
            <section style={{ padding: '80px 24px', maxWidth: '1200px', margin: '0 auto' }}>
              {[0,1,2]?.map(i => <CaseStudySkeleton key={i} />)}
            </section>
            <TestimonialsSkeleton />
          </>
        ) : (
          <>
            <StoriesHero />

            {/* Divider */}
            <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
              <div style={{ height: '1px', background: 'linear-gradient(to right, transparent, rgba(255,87,34,0.3), transparent)' }} />
            </div>

            {/* Client logos */}
            <ClientLogosGrid />

            {/* Divider */}
            <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
              <div style={{ height: '1px', background: 'linear-gradient(to right, transparent, rgba(0,79,152,0.3), transparent)' }} />
            </div>

            {/* Case studies */}
            <section style={{ padding: '80px 24px', maxWidth: '1200px', margin: '0 auto' }}>
              <div style={{ marginBottom: '48px', display: 'flex', alignItems: 'flex-start', gap: '24px' }}>
                <div style={{ flexShrink: 0, marginTop: '6px' }}>
                  <div style={{ width: '3px', height: '48px', background: '#004F98', borderRadius: '2px' }} />
                </div>
                <div>
                  <span style={{ fontFamily: 'var(--font-heading)', fontSize: '11px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#004F98', display: 'block', marginBottom: '8px' }}>Detaljerte casestudier</span>
                  <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '36px', fontWeight: 700, color: 'var(--color-foreground)', marginBottom: '12px', letterSpacing: '-0.02em' }}>Før og etter — reelle tall</h2>
                  <p style={{ fontFamily: 'var(--font-body)', fontSize: '16px', color: 'var(--color-muted-foreground)', maxWidth: '560px', lineHeight: 1.6 }}>Detaljerte gjennomganger av hvordan norske bedrifter transformerte sin bompengehåndtering med dokumenterte besparelser i norske kroner.</p>
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                {caseStudies?.map((study, i) => (
                  <CaseStudyCard key={study?.company} study={study} index={i} />
                ))}
              </div>
            </section>

            {/* Divider */}
            <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
              <div style={{ height: '1px', background: 'linear-gradient(to right, transparent, rgba(255,87,34,0.3), transparent)' }} />
            </div>

            {/* Testimonials */}
            <TestimonialsSection />

            {/* CTA */}
            <section style={{ padding: '0 24px 80px', maxWidth: '1200px', margin: '0 auto' }}>
              <div style={{ padding: '48px', background: 'linear-gradient(135deg, rgba(255,87,34,0.08) 0%, rgba(0,79,152,0.08) 100%)', border: '1px solid rgba(255,87,34,0.2)', borderRadius: '2px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '32px', flexWrap: 'wrap' }}>
                <div>
                  <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '24px', fontWeight: 700, color: 'var(--color-foreground)', marginBottom: '8px' }}>Bli vår neste suksesshistorie</h2>
                  <p style={{ fontFamily: 'var(--font-body)', fontSize: '15px', color: 'var(--color-muted-foreground)' }}>Start gratis i dag og se hvor mye du kan spare på norske veier.</p>
                </div>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  <button onClick={() => navigate('/register')} style={{ background: '#FF5722', border: '2px solid #FF5722', borderRadius: '2px', padding: '12px 28px', fontFamily: 'var(--font-heading)', fontSize: '13px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#FFFFFF', cursor: 'pointer' }}>Kom i gang gratis</button>
                  <button onClick={() => navigate('/contact')} style={{ background: 'transparent', border: '1px solid rgba(224,228,232,0.2)', borderRadius: '2px', padding: '12px 28px', fontFamily: 'var(--font-heading)', fontSize: '13px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--color-foreground)', cursor: 'pointer' }}>Kontakt oss</button>
                </div>
              </div>
            </section>
          </>
        )}
      </main>
      {/* Footer */}
      <footer style={{ background: 'rgba(255,255,255,0.02)', borderTop: '1px solid rgba(224,228,232,0.06)', padding: '32px 32px 24px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: '24px', height: '24px', background: '#FF5722', borderRadius: '2px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2.5"><polygon points="3 11 22 2 13 21 11 13 3 11" /></svg>
            </div>
            <span style={{ fontFamily: 'var(--font-heading)', fontSize: '14px', fontWeight: 700, color: 'var(--color-foreground)' }}>Veiassistent</span>
          </div>
          <span style={{ fontFamily: 'var(--font-caption)', fontSize: '12px', color: 'var(--color-muted-foreground)' }}>© 2026 Veiassistent AS. Alle rettigheter forbeholdt.</span>
        </div>
      </footer>
    </div>
  );
};

export default CustomerStories;
